import React, { useEffect, useMemo, useRef, useState } from "react";
import { searchPortal } from "./api";
import { buildPortalHash } from "./betaRouting";
import { PortalEmptyState, PortalSearchIcon } from "./PortalExperience";
import "./GlobalSearch.css";

const RECENT_KEY = "urban-portal-global-search-recents";
const GROUP_LABELS = {
    episode: "Episodes",
    podcast: "Podcasts",
    client: "Clients",
    lead: "Leads",
    meeting: "Meetings",
    recording: "Recording sessions",
    file: "Files",
    invoice: "Invoices",
    navigation: "Go to",
    action: "Actions",
};

const PAGE_COMMANDS = [
    ["dashboard", "Dashboard", "Overview and deadlines"],
    ["episodes", "Episodes", "Production and client review"],
    ["podcasts", "Podcasts", "Podcast settings and schedules"],
    ["analytics", "Analytics", "Captivate performance"],
    ["clients", "Clients", "Client accounts"],
    ["leads", "Leads", "Enquiries and onboarding"],
    ["meetings", "Meetings", "Scheduling requests"],
    ["recording-sessions", "Recording Sessions", "Remote studio and recordings"],
    ["files", "File Explorer", "Browse production files"],
    ["user-management", "User Management", "Users and permissions"],
    ["billing", "Billing", "Invoices and Xero"],
    ["settings", "Settings", "Account and portal settings"],
];

const ACTION_COMMANDS = [
    ["recording-sessions/new", "Create recording session", "Open the studio session form"],
    ["recording-sessions/solo/new", "Start a solo recording", "Open the solo recorder setup"],
    ["meetings", "Create meeting request", "Open Meetings to create a scheduling link"],
    ["episodes", "Create episode", "Open Episodes to add an episode"],
];

function focusAndSelect(inputRef) {
    window.requestAnimationFrame(() => {
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        input.select();
    });
}

function focusAtEnd(inputRef) {
    window.requestAnimationFrame(() => {
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        const end = input.value.length;
        input.setSelectionRange(end, end);
    });
}

function readRecents(userId) {
    try {
        const stored = JSON.parse(window.localStorage.getItem(`${RECENT_KEY}:${userId || "user"}`) || "[]");
        return Array.isArray(stored) ? stored.slice(0, 6) : [];
    } catch {
        return [];
    }
}

function saveRecent(userId, item) {
    if (!item || !item.url || item.type === "navigation" || item.type === "action") return;
    const current = readRecents(userId);
    const key = `${item.type}:${item.id}`;
    const next = [item, ...current.filter((entry) => `${entry.type}:${entry.id}` !== key)].slice(0, 6);
    try {
        window.localStorage.setItem(`${RECENT_KEY}:${userId || "user"}`, JSON.stringify(next));
    } catch {
        // Recent items are an enhancement; navigation still works without storage.
    }
}

function commandResults(query, user = { role: "admin" }) {
    const value = String(query || "").trim().toLowerCase();
    const isAdmin = String(user?.role || "").toLowerCase() === "admin";
    const clientRoutes = new Set([
        "dashboard",
        "episodes",
        "analytics",
        "recording-sessions",
        "files",
        "billing",
        "settings",
    ]);
    const makeItems = (commands, type) => commands.map(([route, title, subtitle]) => ({
        type,
        id: route,
        title,
        subtitle,
        meta: type === "action" ? "Action" : "Page",
        url: `#/${route}`,
    }));
    const visiblePages = isAdmin
        ? PAGE_COMMANDS
        : PAGE_COMMANDS.filter(([route]) => {
            if (!clientRoutes.has(route)) return false;
            if (route === "analytics") return Boolean(user?.has_analytics_access);
            if (route === "billing") return Boolean(user?.has_billing_access);
            if (route === "files") return Boolean(user?.has_file_browser_access);
            if (route === "recording-sessions") return Boolean(user?.has_recording_sessions_access);
            return true;
        });
    const visibleActions = isAdmin ? ACTION_COMMANDS : [];
    const items = [...makeItems(visiblePages, "navigation"), ...makeItems(visibleActions, "action")];
    if (!value) return items.slice(0, 6);
    return items.filter((item) => `${item.title} ${item.subtitle}`.toLowerCase().includes(value));
}

function groupResults(items) {
    const groups = [];
    for (const item of items) {
        let group = groups.find((entry) => entry.type === item.type);
        if (!group) {
            group = { type: item.type, label: GROUP_LABELS[item.type] || "Results", items: [] };
            groups.push(group);
        }
        group.items.push(item);
    }
    return groups;
}

export default function GlobalSearch({ currentUser, variant = "modern" }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [remoteResults, setRemoteResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const desktopInputRef = useRef(null);
    const mobileInputRef = useRef(null);
    const rootRef = useRef(null);
    const requestRef = useRef(0);
    const isAdmin = String(currentUser?.role || "").toLowerCase() === "admin";

    useEffect(() => {
        if (!isAdmin) return undefined;
        const handleShortcut = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                setOpen(true);
                if (window.innerWidth > 900) {
                    focusAndSelect(desktopInputRef);
                }
            }
        };
        window.addEventListener("keydown", handleShortcut);
        return () => window.removeEventListener("keydown", handleShortcut);
    }, [isAdmin]);

    useEffect(() => {
        if (!open) return undefined;
        const isMobile = window.innerWidth <= 900;
        const previousOverflow = document.body.style.overflow;
        if (isMobile) {
            document.body.style.overflow = "hidden";
            focusAtEnd(mobileInputRef);
        }
        const closeOnEscape = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        const closeOnOutsideClick = (event) => {
            if (!isMobile && rootRef.current && !rootRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        window.addEventListener("keydown", closeOnEscape);
        document.addEventListener("pointerdown", closeOnOutsideClick);
        return () => {
            if (isMobile) document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", closeOnEscape);
            document.removeEventListener("pointerdown", closeOnOutsideClick);
        };
    }, [open]);

    useEffect(() => {
        if (!open || query.trim().length < 2) {
            requestRef.current += 1;
            setRemoteResults([]);
            setLoading(false);
            setError("");
            return undefined;
        }
        const requestId = ++requestRef.current;
        const controller = new AbortController();
        const timer = window.setTimeout(async () => {
            setLoading(true);
            setError("");
            try {
                const response = await searchPortal(query, { signal: controller.signal });
                if (requestId !== requestRef.current) return;
                if (!response?.ok) throw new Error(response?.message || "Search failed");
                setRemoteResults(Array.isArray(response.results) ? response.results : []);
            } catch (searchError) {
                if (requestId !== requestRef.current) return;
                setRemoteResults([]);
                setError(searchError?.message || "Search is temporarily unavailable");
            } finally {
                if (requestId === requestRef.current) setLoading(false);
            }
        }, 200);
        return () => {
            window.clearTimeout(timer);
            requestRef.current += 1;
            controller.abort();
        };
    }, [open, query]);

    const items = useMemo(() => {
        const commands = commandResults(query, currentUser);
        if (!query.trim()) {
            const recents = readRecents(currentUser?.id).map((item) => ({ ...item, type: item.type || "recent" }));
            return [...commands, ...recents];
        }
        return [...commands, ...remoteResults];
    }, [currentUser?.id, query, remoteResults]);
    const groups = useMemo(() => groupResults(items), [items]);

    useEffect(() => setActiveIndex(0), [items.length, query]);

    if (!isAdmin) return null;

    const selectItem = (item) => {
        if (!item) return;
        saveRecent(currentUser?.id, item);
        setOpen(false);
        setQuery("");
        window.location.hash = buildPortalHash(item.url, window.location.hash);
    };

    const onInputKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((index) => Math.min(items.length - 1, index + 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((index) => Math.max(0, index - 1));
        } else if (event.key === "Enter" && items[activeIndex]) {
            event.preventDefault();
            selectItem(items[activeIndex]);
        }
    };

    const renderResults = (resultsId) => {
        let renderedIndex = -1;
        return (
            <>
                <div className="global-search-results" id={resultsId}>
                    {!query.trim() ? <p className="global-search-hint">Quick navigation and recently opened records</p> : null}
                    {error ? <div className="global-search-message global-search-message--error">{error}</div> : null}
                    {groups.map((group) => (
                        <div className="global-search-group" key={group.type}>
                            <h3>{group.label}</h3>
                            {group.items.map((item) => {
                                renderedIndex += 1;
                                const itemIndex = renderedIndex;
                                return (
                                    <button
                                        type="button"
                                        className={`global-search-result${itemIndex === activeIndex ? " is-active" : ""}`}
                                        key={`${item.type}:${item.id}`}
                                        onMouseEnter={() => setActiveIndex(itemIndex)}
                                        onClick={() => selectItem(item)}
                                    >
                                        <span className={`global-search-result__icon global-search-result__icon--${item.type}`} aria-hidden="true"><PortalSearchIcon /></span>
                                        <span className="global-search-result__copy">
                                            <strong>{item.title}</strong>
                                            {item.subtitle ? <span>{item.subtitle}</span> : null}
                                        </span>
                                        {item.meta ? <span className="global-search-result__meta">{String(item.meta).replace(/_/g, " ")}</span> : null}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                    {query.trim().length === 1 ? <div className="global-search-message">Type one more character to search portal records.</div> : null}
                    {query.trim().length >= 2 && !loading && !error && items.length === 0 ? (
                        <PortalEmptyState
                            title="No portal results found"
                            description="Try a different name, reference, or page command."
                            compact
                        />
                    ) : null}
                </div>
                <footer className="global-search-footer"><span><kbd>↑</kbd><kbd>↓</kbd> Navigate</span><span><kbd>↵</kbd> Open</span><span><kbd>Esc</kbd> Close</span></footer>
            </>
        );
    };

    return (
        <>
            <div className={`global-search-desktop global-search-desktop--${variant}`} ref={rootRef}>
                <label className="global-search-field">
                    <PortalSearchIcon />
                    <input
                        ref={desktopInputRef}
                        type="search"
                        value={query}
                        onFocus={() => {
                            if (!open) {
                                setOpen(true);
                                focusAndSelect(desktopInputRef);
                            }
                        }}
                        onClick={() => {
                            if (!open) {
                                setOpen(true);
                                focusAndSelect(desktopInputRef);
                            }
                        }}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            setOpen(true);
                        }}
                        onKeyDown={onInputKeyDown}
                        placeholder="Search…"
                        aria-label="Search the portal"
                        aria-controls="global-search-desktop-results"
                        aria-expanded={open}
                    />
                    {loading ? <span className="global-search-loading">Searching…</span> : <kbd>⌘K</kbd>}
                </label>
                {open ? <section className="global-search-popover" aria-label="Search results">{renderResults("global-search-desktop-results")}</section> : null}
            </div>
            <button type="button" className="global-search-mobile-trigger" onClick={() => setOpen(true)} aria-label="Search the portal" title="Search the portal">
                <PortalSearchIcon />
            </button>
            {open ? (
                <div className="global-search-overlay" role="presentation" onMouseDown={(event) => {
                    if (event.target === event.currentTarget) setOpen(false);
                }}>
                    <section className="global-search-dialog" role="dialog" aria-modal="true" aria-label="Search the portal">
                        <div className="global-search-input-row">
                            <PortalSearchIcon />
                            <input
                                ref={mobileInputRef}
                                type="search"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                onKeyDown={onInputKeyDown}
                                placeholder="Search episodes, clients, files, invoices…"
                                aria-label="Search the portal"
                                aria-controls="global-search-mobile-results"
                            />
                            {loading ? <span className="global-search-loading">Searching…</span> : null}
                            <button type="button" className="global-search-close" onClick={() => setOpen(false)} aria-label="Close search">Esc</button>
                        </div>
                        {renderResults("global-search-mobile-results")}
                    </section>
                </div>
            ) : null}
        </>
    );
}

export { commandResults, groupResults };
